import { useFetchApiRequest, useFetchApiRequestAsync } from 'auth-package'
import { ManageProfile } from 'auth-package'
import { defineStore } from 'pinia'
import { EConsoleType, useConsoleMessage } from '@/composables/useConsoleMessage'
import { ElMessage } from 'element-plus'

interface AccountState {
    account: Account
    userCredentials: UserCredentials[]
    errors: any[]
}

interface UserCredentials {
    type: string
    userCredentialMetadatas: UserCredentialMetadatas[]
}

interface UserCredentialMetadatas {
    credential: Credential
}

interface Credential {
    createdDate: number
    credentialData: string
    userLabel: string
    type: string
}

export interface Account {
    id: string
    email: string
    emailVerified: boolean
    firstName: string
    lastName: string
    username: string
    attributes: {
        profileImage?: string
    }
}

export const useAccountStore = defineStore('account', {
    state: (): AccountState => {
        return {
            account: { attributes: { profileImage: undefined } } as Account,
            userCredentials: [],
            errors: []
        }
    },
    getters: {
        /**
         * Get first name and last name from state
         *
         * @param state
         * @returns A full name (e.g.: John Doe)
         */
        fullName(state) {
            return `${state.account.firstName} ${state.account.lastName}` //TODO: I18n in the next version.
        },
        /**
         * Get user account from state
         *
         * @param state
         * @returns
         */
        userAccount(state) {
            return state.account ?? ({} as Account)
        },
        /**
         * Get user credentials meta data from state
         *
         * @param state
         * @returns
         */
        userCredentialsMetaData(state) {
            let userCredentialMetadatas: UserCredentialMetadatas[] = []
            state.userCredentials.filter((userCredential) => {
                if (userCredential.type == 'password') {
                    userCredentialMetadatas = userCredential.userCredentialMetadatas
                }
            })

            return userCredentialMetadatas
        }
    },
    actions: {
        /**
         * Get the account details from the server
         */
        async getUserAccount() {
            const getUserAccount = ManageProfile.GetUserAccount()
            try {
                const response = await useFetchApiRequestAsync(getUserAccount)
                this.account = response

                useConsoleMessage(EConsoleType.SUCCESS, 'getUserAccount', response)
            } catch (error: any) {
                useConsoleMessage(EConsoleType.ERROR, 'getUserAccount', error)
            }
        },
        /**
         * Update the account details
         */
        async updateUserAccount(account: Account) {
            const updateUserAccount = ManageProfile.UpdateUserAccount().setData(account)

            try {
                await useFetchApiRequestAsync(updateUserAccount)
                this.getUserAccount()

                ElMessage({
                    message: 'User account updated successfully!',
                    type: 'success',
                    grouping: true
                })
                useConsoleMessage(
                    EConsoleType.SUCCESS,
                    'updateUserAccount',
                    'Successfully updated the user account!'
                )
            } catch (error: any) {
                ElMessage({
                    message: 'Failed to update user account!',
                    type: 'error',
                    grouping: true
                })
                useConsoleMessage(EConsoleType.ERROR, 'updateUserAccount', error)
            }
        },
        /**
         * Get the user crendentials from the server
         */
        async getUserCredentials() {
            const getUserCredentials = ManageProfile.GetUserCredentials()

            try {
                const response: UserCredentials[] =
                    await useFetchApiRequestAsync(getUserCredentials)
                this.userCredentials = response
                useConsoleMessage(EConsoleType.SUCCESS, 'getUserCredentials', response)
            } catch (error: any) {
                useConsoleMessage(EConsoleType.ERROR, 'getUserCredentials', error)
            }
        }
    }
})
